'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[100] bg-[rgba(14,24,44,0.60)] backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: 'md' | 'lg' | 'screen'
    onClose?: () => void
    closeButtonClassName?: string
    closeOnOverlay?: boolean
    overlayOnClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    overlayClassName?: string
  }
>(
  (
    {
      className,
      children,
      size,
      closeButtonClassName,
      onClose,
      closeOnOverlay,
      overlayOnClick,
      overlayClassName,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay
        onClick={
          overlayOnClick ??
          (closeOnOverlay === true && onClose ? onClose : undefined)
        }
        tabIndex={-1}
        className={overlayClassName}
      />
      <DialogPrimitive.Content
        ref={ref}
        tabIndex={-1}
        className={cn(
          'fixed left-[50%] top-[50%] z-[100] grid max-h-[calc(100vh-40px)] w-[calc(100dvw-40px)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-md bg-white p-5 shadow-card duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] md:p-7 md:pb-10 lg:max-h-[unset] lg:w-[calc(100dvw-80px)] lg:max-w-lg lg:rounded-lg lg:border lg:border-gray-100 dark:border-gray-700',
          {
            'lg:max-w-lg': size === 'md',
            'lg:max-w-[715px]': size === 'lg',
            'h-[calc(100dvh-80px)] lg:max-h-[768px] lg:max-w-[1024px] print:max-h-[auto] print:max-w-[auto]':
              size === 'screen',
          },
          className,
        )}
        {...props}
      >
        {children}
        <Button
          asChild
          variant="secondary"
          size="icon"
          className={cn(
            'absolute right-3 top-3 size-8 cursor-pointer lg:right-6 lg:top-6 lg:size-11 print:hidden',
            closeButtonClassName,
          )}
          onClick={onClose ? onClose : undefined}
          tabIndex={-1}
        >
          <DialogPrimitive.Close>
            <X className="size-5" strokeWidth={1.2} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </Button>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 md:gap-0',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-medium leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-gray-500', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
